import { isPageElementKind } from '@streamlain/database-identity';
import type * as Y from 'yjs';
import { z } from 'zod';

import { Boxed, Text } from '../../reactive/index.js';
import type { BaseBlockTransformer } from '../../transformer/base.js';
import type { BlockModel } from './block-model.js';

// Block flavours are namespaced (`affine:note`): the page structure tells a
// block from a canvas element (`shape`) by its kind.
const FlavourSchema = z
  .string()
  .refine(flavour => !isPageElementKind(flavour), {
    message: 'A block flavour is namespaced, as in affine:note',
  });
const ParentSchema = z.array(z.string()).optional();
const ContentSchema = z.array(z.string()).optional();
const RoleSchema = z.string();

export type RoleType = 'root' | 'content' | string;

export interface InternalPrimitives {
  Text: (input?: Y.Text | string) => Text;
  Boxed: <T>(input: T) => Boxed<T>;
}

export const internalPrimitives: InternalPrimitives = Object.freeze({
  Text: (input: Y.Text | string = '') => new Text(input),
  Boxed: <T>(input: T) => new Boxed(input),
});

/**
 * Props of a block that live in the page structure, not in its fields: the
 * store writes them there when the block is added and the transformer reads
 * them back into the snapshot.
 */
export type BlockStructureProps = {
  /** Prop whose Record<id, true> names the members: each one's entry g. */
  members?: string;
  /** Prop holding the presentation index: the block entry's pi. */
  presentationIndex?: string;
  /** Prop whose Boxed Y.Map holds the canvas elements drawn on the block. */
  elements?: string;
};

/**
 * A function option or field: zod checks that the value is callable, the
 * signature is its TypeScript type.
 */
export function functionSchema<T extends (...args: never[]) => unknown>() {
  return z.custom<T>(value => typeof value === 'function');
}

const StructurePropsSchema = z
  .object({
    members: z.string().optional(),
    presentationIndex: z.string().optional(),
    elements: z.string().optional(),
  })
  .optional();

export const BlockSchema = z.object({
  version: z.number(),
  model: z.object({
    role: RoleSchema,
    flavour: FlavourSchema,
    parent: ParentSchema,
    children: ContentSchema,
    isFlatData: z.boolean().optional(),
    documentPart: z.boolean().optional(),
    structure: StructurePropsSchema,
    props:
      functionSchema<
        (internalPrimitives: InternalPrimitives) => Record<string, any>
      >().optional(),
    toModel: functionSchema<() => BlockModel>().optional(),
  }),
  transformer:
    functionSchema<
      (transformerConfig: Map<string, unknown>) => BaseBlockTransformer
    >().optional(),
});

export type BlockSchemaType = z.infer<typeof BlockSchema>;

export type PropsGetter<Props> = (
  internalPrimitives: InternalPrimitives
) => Props;

export function defineBlockSchema<
  Flavour extends string,
  Role extends RoleType,
  Props extends object,
  Metadata extends Readonly<{
    version: number;
    role: Role;
    parent?: string[];
    children?: string[];
    isFlatData?: boolean;
    /** One block per page, kept in the page document part, never in a part that unloads. */
    documentPart?: boolean;
    structure?: BlockStructureProps;
  }>,
  Model extends BlockModel<Props>,
  Transformer extends BaseBlockTransformer<Props>,
>(options: {
  flavour: Flavour;
  metadata: Metadata;
  props?: (internalPrimitives: InternalPrimitives) => Props;
  toModel?: () => Model;
  transformer?: (transformerConfig: Map<string, unknown>) => Transformer;
}): {
  version: number;
  model: {
    props: PropsGetter<Props>;
    flavour: Flavour;
  } & Metadata;
  transformer?: (transformerConfig: Map<string, unknown>) => Transformer;
};

export function defineBlockSchema({
  flavour,
  props,
  metadata,
  toModel,
  transformer,
}: {
  flavour: string;
  metadata: {
    version: number;
    role: RoleType;
    parent?: string[];
    children?: string[];
    isFlatData?: boolean;
    /** One block per page, kept in the page document part, never in a part that unloads. */
    documentPart?: boolean;
    structure?: BlockStructureProps;
  };
  props?: (internalPrimitives: InternalPrimitives) => Record<string, unknown>;
  toModel?: () => BlockModel;
  transformer?: (
    transformerConfig: Map<string, unknown>
  ) => BaseBlockTransformer;
}): BlockSchemaType {
  const schema = {
    version: metadata.version,
    model: {
      role: metadata.role,
      parent: metadata.parent,
      children: metadata.children,
      flavour,
      props,
      toModel,
      isFlatData: metadata.isFlatData,
      documentPart: metadata.documentPart,
      structure: metadata.structure,
    },
    transformer,
  } satisfies z.infer<typeof BlockSchema>;
  BlockSchema.parse(schema);
  return schema;
}
